/* Keep coupon state in Shopify's cart, including across currency and drawer updates. */
(() => {
  if (customElements.get('kt-cart-coupon')) return;

  const SECTION = 'cart-drawer';
  const FRAGMENTS = [
    '[data-minimog-cart-items]',
    '[data-minimog-cart-discounts]',
    '[data-cart-subtotal]',
    '[data-minimog-gift-wrapping]'
  ];
  const MUTATING_CONTROLS = [
    '.m-quantity', 'm-cart-remove-button', 'm-gift-wrapping-component',
    '.flexy-checkout-btn', '[name="checkout"]', '[data-terms-action]',
    'a[href*="/checkout"]', '.additional-checkout-buttons', '[name="agree_terms"]'
  ].join(',');
  const LOCKABLE_CONTROLS = [
    '.m-quantity input', '.m-quantity button', 'm-cart-remove-button',
    'm-gift-wrapping-component input', 'm-gift-wrapping-component textarea',
    '.flexy-checkout-btn', '[name="checkout"]', '[data-terms-action]',
    'a[href*="/checkout"]', '.additional-checkout-buttons', '[name="agree_terms"]'
  ].join(',');
  const FALLBACK_MESSAGES = {
    empty: 'Enter a coupon code.',
    separate: 'Enter one coupon code at a time.',
    invalid: 'This code cannot be applied to your cart. Check the code and its conditions.',
    applied: 'Coupon applied.',
    removed: 'Coupon removed.',
    error: 'We could not update your cart. Please try again.',
    pending: 'Updating your cart…',
    already: 'This coupon is already applied.',
    shipping: 'Code accepted. Shipping is calculated at checkout.'
  };

  class KtCartCoupon extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.form = this.querySelector('form');
      this.input = this.querySelector('[data-coupon-input]');
      this.status = this.querySelector('[data-coupon-status]');
      this.codesList = this.querySelector('[data-coupon-codes]');
      this.drawer = this.closest('m-cart-drawer');
      if (!this.form || !this.input || !this.status || !this.codesList || !this.drawer) return;

      this.connected = true;
      this.busy = false;
      this.syncQueued = false;
      this.lockedControls = new Map();
      this.lifecycle = new AbortController();
      this.generation = (this.generation || 0) + 1;
      const options = { signal: this.lifecycle.signal };
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.apply();
      }, { ...options, capture: true });
      this.input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || event.isComposing) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.apply();
      }, { ...options, capture: true });
      ['input', 'change'].forEach((name) => {
        this.addEventListener(name, (event) => {
          if (event.target === this.input) event.stopImmediatePropagation();
        }, { ...options, capture: true });
      });
      this.addEventListener('click', (event) => {
        const applyButton = event.target.closest('[data-coupon-apply]');
        if (applyButton && this.contains(applyButton)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.apply();
          return;
        }
        const button = event.target.closest('[data-coupon-remove]');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.change('remove', button.dataset.couponRemove);
      }, { ...options, capture: true });

      // Capture custom-element removal and accelerated-checkout clicks as well as native inputs.
      const guardCartAction = (event) => {
        if (!this.busy) return;
        const target = event.target;
        if (target.closest?.(MUTATING_CONTROLS) || (event.type === 'submit' && target.id === 'cart-drawer-form')) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      };
      ['click', 'change', 'submit'].forEach((name) => {
        this.drawer.addEventListener(name, guardCartAction, { ...options, capture: true });
      });
      document.addEventListener('cart:updated', (event) => {
        const cart = event.detail?.cart;
        if (cart && cart === this.emittedCart) return;
        if (this.busy) {
          this.syncQueued = true;
        } else if (cart && Array.isArray(cart.discount_codes)) {
          this.renderCodes(cart);
        } else {
          this.sync();
        }
      }, options);
      const theme = typeof MinimogTheme !== 'undefined' ? MinimogTheme : window.MinimogTheme;
      const openEvent = theme?.pubSubEvents?.openCartDrawer;
      if (window.MinimogEvents?.subscribe && openEvent) {
        this.unsubscribeOpen = window.MinimogEvents.subscribe(openEvent, () => this.sync());
      }
      this.sync();
    }

    disconnectedCallback() {
      this.connected = false;
      this.generation = (this.generation || 0) + 1;
      this.lifecycle?.abort();
      this.requestController?.abort();
      if (typeof this.unsubscribeOpen === 'function') this.unsubscribeOpen();
      this.unsubscribeOpen = null;
      this.syncQueued = false;
      this.setBusy(false);
    }

    rootPath() {
      const root = window.Shopify?.routes?.root || '/';
      return root.endsWith('/') ? root : `${root}/`;
    }

    key(code) {
      return typeof code === 'string' ? code.trim().toLowerCase() : '';
    }

    applications(cart) {
      return [
        ...(cart.cart_level_discount_applications || []),
        ...(cart.items || []).flatMap((item) => [
          ...(item.line_level_discount_allocations || []).map((entry) => entry.discount_application),
          ...(item.discount_allocations || []).map((entry) => entry.discount_application),
          ...(item.discounts || [])
        ])
      ].filter(Boolean);
    }

    acceptedCodes(cart) {
      const entries = Array.isArray(cart.discount_codes)
        ? cart.discount_codes.filter((entry) => entry.applicable === true).map((entry) => entry.code)
        : this.applications(cart).filter((entry) => entry.type === 'discount_code').map((entry) => entry.code || entry.title);
      const seen = new Set();
      return entries.filter((code) => {
        const key = this.key(code);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      }).map((code) => code.trim());
    }

    isShippingCode(cart, code) {
      const applications = this.applications(cart).filter((entry) => this.key(entry.code || entry.title) === this.key(code));
      // Shopify can accept a shipping code without adding a product or order allocation to the cart.
      return !applications.length || applications.some((entry) => entry.target_type === 'shipping_line');
    }

    setStatus(message, state = '') {
      if (!this.status) return;
      this.status.textContent = message ? this.dataset[message] || FALLBACK_MESSAGES[message] || '' : '';
      this.status.dataset.state = state;
      this.input?.setAttribute('aria-invalid', String(['empty', 'separate', 'invalid'].includes(message)));
    }

    renderCodes(cart) {
      const fragment = document.createDocumentFragment();
      this.acceptedCodes(cart).forEach((code) => {
        const item = document.createElement('li');
        const label = document.createElement('span');
        label.textContent = code;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.couponRemove = code;
        const removeLabel = this.dataset.removeLabel || 'Remove {code}';
        remove.setAttribute('aria-label', removeLabel.replace(/\{\{\s*code\s*\}\}|\{code\}/g, () => code));
        const icon = document.createElement('span');
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = '×';
        remove.append(icon);
        item.append(label, remove);
        fragment.append(item);
      });
      this.codesList.replaceChildren(fragment);
      this.lockControls();
    }

    lockControls() {
      if (!this.busy || !this.drawer) return;
      const controls = new Set([
        ...this.querySelectorAll('input, button'),
        ...this.drawer.querySelectorAll(LOCKABLE_CONTROLS)
      ]);
      controls.forEach((control) => {
        if (!this.lockedControls.has(control)) {
          this.lockedControls.set(control, {
            disabled: 'disabled' in control ? control.disabled : null,
            ariaDisabled: control.getAttribute('aria-disabled'),
            tabindex: control.getAttribute('tabindex'),
            inert: control.inert
          });
        }
        if ('disabled' in control) control.disabled = true;
        else {
          control.inert = true;
          control.setAttribute('tabindex', '-1');
        }
        control.setAttribute('aria-disabled', 'true');
      });
    }

    setBusy(busy) {
      this.busy = busy;
      this.setAttribute('aria-busy', String(busy));
      if (busy) {
        this.lockControls();
        this.lockObserver = new MutationObserver(() => this.lockControls());
        this.lockObserver.observe(this.drawer, { childList: true, subtree: true });
      } else {
        this.lockObserver?.disconnect();
        this.lockObserver = null;
        this.lockedControls?.forEach((previous, control) => {
          if (previous.disabled !== null) control.disabled = previous.disabled;
          else control.inert = previous.inert;
          ['aria-disabled', 'tabindex'].forEach((attribute) => {
            const value = attribute === 'aria-disabled' ? previous.ariaDisabled : previous.tabindex;
            if (value === null) control.removeAttribute(attribute);
            else control.setAttribute(attribute, value);
          });
        });
        this.lockedControls?.clear();
      }
    }

    async request(path, options = {}, asText = false) {
      const controller = new AbortController();
      this.requestController = controller;
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(`${this.rootPath()}${path}`, {
          credentials: 'same-origin',
          cache: 'no-store',
          ...options,
          signal: controller.signal
        });
        if (!response.ok) throw new Error('Cart request failed');
        const result = asText ? await response.text() : await response.json();
        if (!asText && (!result || !Array.isArray(result.items))) throw new Error('Invalid cart response');
        return result;
      } finally {
        clearTimeout(timeout);
        if (this.requestController === controller) this.requestController = null;
      }
    }

    readCart() {
      return this.request('cart.js', { headers: { Accept: 'application/json' } });
    }

    updateDiscounts(codes) {
      return this.request('cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(','), sections: [SECTION], sections_url: window.location.pathname })
      });
    }

    async refreshDrawer(cart) {
      let html = cart.sections?.[SECTION];
      if (typeof html !== 'string' || !html.trim()) {
        html = await this.request(`cart?section_id=${SECTION}`, { headers: { Accept: 'text/html' } }, true);
      }
      if (!this.connected) throw new Error('Coupon disconnected');
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nativeSections = this.drawer.getSectionsToRender?.() || FRAGMENTS.map((selector) => ({ selector }));
      const replacements = nativeSections.filter((section) => FRAGMENTS.includes(section.selector)).map((section) => {
        const target = this.drawer.querySelector(section.selector);
        const source = parsed.querySelector(section.selector);
        if (!target || !source) throw new Error('Cart section unavailable');
        return { target, html: source.innerHTML };
      });
      if (replacements.length !== FRAGMENTS.length) throw new Error('Incomplete cart section');
      // Retain the drawer, footer and coupon component; server markup retains all currency hooks.
      replacements.forEach(({ target, html: contents }) => { target.innerHTML = contents; });
      this.lockControls();
      this.drawer.classList.toggle('m-cart--empty', cart.item_count === 0);
      this.drawer.updateCartCount?.(cart.item_count);
    }

    finish(generation) {
      if (generation !== this.generation) return;
      this.setBusy(false);
      if (this.syncQueued && this.connected) {
        this.syncQueued = false;
        this.sync();
      }
    }

    async sync() {
      if (!this.connected) return;
      if (this.busy) {
        this.syncQueued = true;
        return;
      }
      const generation = this.generation;
      this.setBusy(true);
      try {
        const cart = await this.readCart();
        if (generation !== this.generation || !this.connected) return;
        this.renderCodes(cart);
        // Keep the outcome of the last customer action visible after a cart/drawer event.
        if (this.status.dataset.state === 'error') this.setStatus('');
      } catch (error) {
        if (generation === this.generation && this.connected) this.setStatus('error', 'error');
      } finally {
        this.finish(generation);
      }
    }

    apply() {
      if (this.busy) return;
      const code = this.input.value.trim();
      if (!code) {
        this.setStatus('empty', 'error');
        this.input.focus();
        return;
      }
      if (code.includes(',')) {
        this.setStatus('separate', 'error');
        this.input.focus();
        return;
      }
      return this.change('apply', code);
    }

    async change(action, code) {
      if (this.busy || !this.connected || !this.key(code)) return;
      const generation = this.generation;
      const hadFocus = this.contains(document.activeElement);
      this.setBusy(true);
      this.setStatus('pending', 'pending');
      let mutationAttempted = false;
      try {
        const before = await this.readCart();
        if (generation !== this.generation || !this.connected) return;
        const previous = this.acceptedCodes(before);
        const exists = previous.some((entry) => this.key(entry) === this.key(code));
        if (action === 'apply' && exists) {
          this.renderCodes(before);
          this.setStatus('already', 'success');
          return;
        }
        if (action === 'remove' && !exists) {
          this.renderCodes(before);
          this.setStatus('removed', 'success');
          return;
        }
        const desired = action === 'remove'
          ? previous.filter((entry) => this.key(entry) !== this.key(code))
          : [...previous, code.trim()];
        mutationAttempted = true;
        let cart = await this.updateDiscounts(desired);
        if (generation !== this.generation || !this.connected) return;
        const accepted = this.acceptedCodes(cart).some((entry) => this.key(entry) === this.key(code));
        const rejected = action === 'apply' && !accepted;
        // A rejected combination must not discard a coupon that was already valid.
        if (rejected) cart = await this.updateDiscounts(previous);
        if (generation !== this.generation || !this.connected) return;
        this.renderCodes(cart);
        await this.refreshDrawer(cart);
        if (generation !== this.generation || !this.connected) return;
        const finalCodes = this.acceptedCodes(cart);
        if (action === 'remove' && finalCodes.some((entry) => this.key(entry) === this.key(code))) {
          this.setStatus('error', 'error');
        } else if (rejected) {
          const restored = previous.every((entry) => finalCodes.some((saved) => this.key(saved) === this.key(entry)));
          this.setStatus(restored ? 'invalid' : 'error', 'error');
        } else if (action === 'remove') {
          this.setStatus('removed', 'success');
        } else {
          this.input.value = '';
          this.setStatus(this.isShippingCode(cart, code) ? 'shipping' : 'applied', 'success');
        }
        this.emitUpdated(cart);
      } catch (error) {
        if (generation !== this.generation || !this.connected) return;
        // A lost response may still have changed the server cart. Reconcile without claiming success.
        if (mutationAttempted) {
          try {
            const current = await this.readCart();
            if (generation !== this.generation || !this.connected) return;
            this.renderCodes(current);
            await this.refreshDrawer(current);
            if (generation !== this.generation || !this.connected) return;
            this.emitUpdated(current);
          } catch (recoveryError) { /* The translated error remains visible for a retry. */ }
        }
        if (generation === this.generation && this.connected) this.setStatus('error', 'error');
      } finally {
        this.finish(generation);
        if (generation === this.generation && hadFocus && this.connected && !this.busy && this.drawer.classList.contains('m-cart-drawer--active')) {
          this.input.focus({ preventScroll: true });
        }
      }
    }

    emitUpdated(cart) {
      this.emittedCart = cart;
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      this.emittedCart = null;
    }
  }

  customElements.define('kt-cart-coupon', KtCartCoupon);
})();
